import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Fragment, useEffect, useState } from 'react'
import { saveStepCab, stepCabQuery, workflowCabQuery, type CabRule } from '../../api/changeManagement'
import { ApiError } from '../../api/client'
import {
  saveStepApprover,
  stepApproverQuery,
  workflowApproversQuery,
  type StepApprover,
} from '../../api/easeedeskApprovals'
import {
  createStep,
  createWorkflow,
  groupUsersQuery,
  removeStep,
  removeWorkflow,
  stepOptionsQuery,
  stepQuery,
  updateStep,
  updateWorkflow,
  workflowStepsQuery,
  workflowsShellQuery,
  workflowsTableQuery,
  type StepForm,
  type WorkflowRow,
  type WorkflowStep,
} from '../../api/settingsEaseedeskWorkflows'
import { LoadFailed } from '../../ui/LoadFailed'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings → Easeedesk → Workflow settings.
 *
 * Element cu element fata de `version-3/easeedesk_settings/workflows/
 * workflow_settings/index.php` + `workflow_details.php` + `workflow_steps.php`
 * + `workflow_edit_form.php` + `new_step_form.php` + `edit_step_form.php`, si
 * gest cu gest fata de cele cinci fisiere `*-1.js` de langa ele.
 *
 * PASUL DE DESCHIDERE (`status_from = 0`) se arata "CREATED", nu se sterge, si
 * capatul lui de plecare nu se schimba - la fel ca in portal.
 *
 * PERSOANA BATE GRUPUL: pe randul unui pas se arata persoana cand exista, cu
 * numele grupului langa ea. Altfel un pas cu amandoua completate ar parea ca
 * n-are grup deloc.
 */

const PAGE = 25
const EMPTY_STEP: StepForm = { step_name: '', step_from: 0, step_to: 0, assign_group_id: 0, assign_user_id: 0 }

/** Selectorul de persoana: oamenii grupului ales, ceruti cand se schimba grupul. */
function PersonSelect({
  group,
  value,
  seeded,
  onChange,
}: {
  group: number
  value: number
  /** Lista venita cu pasul, ca sa se vada persoana salvata din prima clipa. */
  seeded: Array<{ ID: number; user_name: string; outside: boolean }>
  onChange(next: number): void
}) {
  const fetched = useQuery(groupUsersQuery(group))
  const users = fetched.data?.users ?? seeded

  return (
    <select
      className="form-control"
      name="assign_user_id"
      value={value === 0 ? '' : String(value)}
      onChange={(e) => onChange(Number(e.target.value))}
    >
      <option value="">— nobody in particular —</option>
      {users.map((user) => (
        <option key={user.ID} value={String(user.ID)}>
          {user.user_name}
          {user.outside ? ' (other group)' : ''}
        </option>
      ))}
    </select>
  )
}

function StepModal({
  workflow,
  step,
  open,
  onClose,
  onSaved,
}: {
  workflow: number
  step?: WorkflowStep
  open: boolean
  onClose(): void
  onSaved(): void
}) {
  const editing = step !== undefined
  const queryClient = useQueryClient()
  const options = useQuery(stepOptionsQuery(open && !editing ? workflow : 0))
  const existing = useQuery(stepQuery(open && editing ? step.ID : 0))
  const [form, setForm] = useState<StepForm>(EMPTY_STEP)
  /*
   * CAB-ul pasului (change management): cand un change trece prin pasul asta,
   * trebuie aprobat de grupul ales, dupa regula aleasa. Pasul nou cere
   * grupurile cu id 0. Fara drept sau fara raspuns, sectiunea nu apare si
   * salvarea pasului nu atinge CAB-ul.
   */
  const cabQuery = useQuery({ ...stepCabQuery(editing ? step.ID : 0, open), retry: false })
  const cabReady = cabQuery.data && Array.isArray(cabQuery.data.groups) ? cabQuery.data : undefined
  const [cab, setCab] = useState<{ group: number; rule: CabRule }>({ group: 0, rule: 'all' })
  /*
   * Cine aproba pasul: "User manager" = managerul din IAM al solicitantului.
   * Tichetul care intra pe pas asteapta hotararea lui. Fara drept sau fara
   * raspuns, sectiunea nu apare si salvarea pasului nu atinge aprobarea.
   */
  const approverQuery = useQuery({ ...stepApproverQuery(editing ? step.ID : 0, open), retry: false })
  const approverReady =
    approverQuery.data && Array.isArray(approverQuery.data.options) ? approverQuery.data : undefined
  const [approver, setApprover] = useState<StepApprover>('')

  useEffect(() => {
    if (open && approverReady) setApprover(approverReady.approver)
  }, [open, approverReady])

  useEffect(() => {
    if (open && cabReady) setCab({ group: cabReady.cab_group_id, rule: cabReady.cab_rule })
  }, [open, cabReady])

  useEffect(() => {
    if (!open) return
    if (!editing) {
      setForm(EMPTY_STEP)
      return
    }
    if (existing.data) {
      setForm({
        step_name: existing.data.step.step_name,
        step_from: existing.data.step.status_from,
        step_to: existing.data.step.status_to,
        assign_group_id: existing.data.step.assign_group_id ?? 0,
        assign_user_id: existing.data.step.assign_user_id ?? 0,
      })
    }
  }, [open, editing, existing.data])

  const save = useMutation({
    mutationFn: async () => {
      const answer = editing ? await updateStep(step.ID, form) : await createStep(workflow, form)
      const stepId = editing ? step.ID : ((answer as { ID?: number }).ID ?? 0)
      const cabChanged = cabReady !== undefined && (cab.group !== cabReady.cab_group_id || cab.rule !== cabReady.cab_rule)
      if (stepId > 0 && cabChanged) await saveStepCab(stepId, cab.group, cab.rule)
      if (stepId > 0 && approverReady !== undefined && approver !== approverReady.approver) {
        await saveStepApprover(stepId, approver)
      }
      return answer
    },
    onSuccess: () => {
      toast(editing ? 'Step updated.' : 'Step created.', 'Workflows')
      void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'change_management'] })
      void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'workflow_settings'] })
      onClose()
      onSaved()
    },
    onError: (e) =>
      toast(e instanceof ApiError && e.message ? e.message : 'Something went wrong.', 'Error', 'error'),
  })

  const lists = editing ? existing.data : options.data
  const isFirst = editing ? (existing.data?.is_first ?? false) : false

  return (
    <Modal
      id={editing ? 'editStepModal' : 'newStepModal'}
      labelledBy={editing ? 'editStepModalLabel' : 'newStepModalLabel'}
      open={open}
      onClose={onClose}
      title={editing ? 'Edit step' : 'New step'}
    >
      {!lists ? (
        <Spinner />
      ) : (
        <form
          id={editing ? 'edit-step-form' : 'new-step-form'}
          method="post"
          onSubmit={(e) => {
            e.preventDefault()
            if (form.step_name.trim() === '' || form.step_to === 0) return
            save.mutate()
          }}
        >
          <div className="form-group">
            <label className="control-label">
              Step name <span className="required">*</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="step_name"
              maxLength={60}
              value={form.step_name}
              onChange={(e) => setForm({ ...form, step_name: e.target.value })}
              required
            />
          </div>

          <div className="form-group">
            <label className="control-label">
              Status from <span className="required">*</span>
            </label>
            {isFirst ? (
              <p className="form-control-static">
                <span className="label" style={{ backgroundColor: '#3a54a3', color: '#fff' }}>
                  CREATED
                </span>{' '}
                <span style={{ color: '#8d8d8d' }}>the opening step does not change where it starts</span>
              </p>
            ) : (
              <select
                className="form-control"
                name="step_from"
                value={form.step_from}
                onChange={(e) => setForm({ ...form, step_from: Number(e.target.value) })}
              >
                <option value={0}>Select..</option>
                {lists.available_from_status.map((status) => (
                  <option key={status.ID} value={status.ID}>
                    {status.status_name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="form-group">
            <label className="control-label">
              Status to <span className="required">*</span>
            </label>
            <select
              className="form-control"
              name="step_to"
              value={form.step_to}
              onChange={(e) => setForm({ ...form, step_to: Number(e.target.value) })}
            >
              <option value={0}>Select..</option>
              {lists.available_to_status.map((status) => (
                <option key={status.ID} value={status.ID}>
                  {status.status_name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="control-label">Assign to group</label>
            <select
              className="form-control"
              name="assign_group_id"
              value={form.assign_group_id === 0 ? '' : String(form.assign_group_id)}
              onChange={(e) =>
                /* Cand se schimba grupul, persoana se goleste - si se vede pe
                   ecran, ca in portal. */
                setForm({ ...form, assign_group_id: Number(e.target.value), assign_user_id: 0 })
              }
            >
              <option value="">— nobody in particular —</option>
              {lists.assign_groups.map((group) => (
                <option key={group.ID} value={String(group.ID)}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label className="control-label">Assign to person</label>
            <PersonSelect
              group={form.assign_group_id}
              value={form.assign_user_id}
              seeded={editing ? (existing.data?.assign_users ?? []) : []}
              onChange={(next) => setForm({ ...form, assign_user_id: next })}
            />
            <span className="help-block" style={{ marginBottom: 0 }}>
              Leave both empty and the step does not change who holds the ticket. A person named here wins over the
              group.
            </span>
          </div>

          {approverReady && (
            <div className="form-group od-step-approver">
              <label className="control-label" htmlFor="step-approver">
                Approval
              </label>
              <select
                id="step-approver"
                className="form-control"
                name="approver"
                value={approver}
                onChange={(e) => setApprover(e.target.value as StepApprover)}
              >
                <option value="">— no approval —</option>
                {approverReady.options.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <span className="help-block" style={{ marginBottom: 0 }}>
                User manager: a ticket that takes this step waits for the manager of its requester, as sent by IAM. With
                no manager in IAM, the manager of the assigned group approves; with neither, a platform administrator.
              </span>
            </div>
          )}

          {cabReady && (
            <fieldset className="form-group od-step-cab">
              <label className="control-label">CAB approval</label>
              <div className="row">
                <div className="col-sm-6">
                  <select
                    className="form-control"
                    name="cab_group_id"
                    aria-label="CAB group"
                    value={cab.group}
                    onChange={(e) => setCab({ ...cab, group: Number(e.target.value) })}
                  >
                    <option value={0}>— no approval —</option>
                    {cabReady.groups.map((group) => (
                      <option key={group.ID} value={group.ID}>
                        {group.name} ({group.members})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-sm-6">
                  <select
                    className="form-control"
                    name="cab_rule"
                    aria-label="CAB rule"
                    value={cab.rule}
                    disabled={cab.group === 0}
                    onChange={(e) => setCab({ ...cab, rule: e.target.value as CabRule })}
                  >
                    {cabReady.rules.map((rule) => (
                      <option key={rule.value} value={rule.value}>
                        {rule.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <span className="help-block" style={{ marginBottom: 0 }}>
                A change request cannot take this step until this CAB group has approved it. Other tickets are not
                affected. Groups are set under{' '}
                <a href="/app/settings/easeedesk/change_management">Change management</a>.
              </span>
            </fieldset>
          )}

          <div className="form-group aright">
            <button type="button" className="btn btn-default" onClick={onClose}>
              Close
            </button>{' '}
            <button
              type="submit"
              className={editing ? 'btn btn-primary submit-edit-step' : 'btn btn-primary submit-new-step'}
              disabled={form.step_name.trim() === '' || form.step_to === 0 || save.isPending}
            >
              {editing ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      )}
    </Modal>
  )
}

function WorkflowDetails({ workflow }: { workflow: WorkflowRow }) {
  const queryClient = useQueryClient()
  const steps = useQuery(workflowStepsQuery(workflow.ID))
  const cab = useQuery({ ...workflowCabQuery(workflow.ID), retry: false })
  const approvers = useQuery({ ...workflowApproversQuery(workflow.ID), retry: false })
  const [creating, setCreating] = useState(false)
  const [edited, setEdited] = useState<WorkflowStep | null>(null)

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'workflow_settings'] })
  }

  const drop = useMutation({
    mutationFn: (id: number) => removeStep(id),
    onSuccess: () => {
      toast('Step removed.', 'Workflows')
      reload()
    },
    onError: (e) =>
      toast(e instanceof ApiError && e.message ? e.message : 'Something went wrong.', 'Error', 'error'),
  })

  const rows = steps.data?.steps ?? []

  return (
    <div>
      <div className="row resetBst">
        <div className="col-md-12">
          <button
            type="button"
            className="btn btn-sm btn-primary pull-right new_step"
            data-workflow={workflow.ID}
            onClick={() => setCreating(true)}
          >
            <i className="fas fa-plus" aria-hidden="true"></i> Add new step
          </button>
          <h4 style={{ marginBottom: 20 }}>
            {/* `body.od-console h4:not([class]) *` pune serif-ul titlului peste tot
                ce e inauntru, iconita inclusa: glifa din zona privata iesea un
                loc gol inaintea numelui. Fontul se fixeaza in linie, ca la NOTE
                din Ticket types - foaia comuna a zonei nu se atinge. */}
            <i
              className="fas fa-external-link-alt"
              aria-hidden="true"
              style={{ fontFamily: '"Font Awesome 5 Free"', fontWeight: 900 }}
            ></i>{' '}
            {workflow.workflow_name}
          </h4>
          <p>{workflow.workflow_description}</p>
        </div>
      </div>

      <hr className="dotted" />

      <div className="row resetBst">
        <div className="col-md-12 workflow-steps">
          {steps.error ? (
            <LoadFailed error={steps.error} onRetry={() => void steps.refetch()} what="The steps" />
          ) : !steps.data ? (
            <Spinner />
          ) : (
            <table className="table workflow-step-list">
              <thead>
                <tr>
                  <th className="a-left">Step</th>
                  <th className="a-left">Status from</th>
                  <th className="a-left"></th>
                  <th className="a-left">Status to</th>
                  <th className="a-left">Handed over to</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: 'center' }}>
                      Nothing to see yet.
                    </td>
                  </tr>
                ) : (
                  rows.map((step) => (
                    <tr key={step.ID}>
                      <td>{step.step_name}</td>
                      <td>
                        {step.status_from === 0 ? (
                          <span className="label" style={{ backgroundColor: '#3a54a3', color: '#fff' }}>
                            CREATED
                          </span>
                        ) : (
                          <span className="label" style={{ backgroundColor: step.st_from_color, color: '#fff' }}>
                            {step.st_from_name}
                          </span>
                        )}
                      </td>
                      <td style={{ color: '#8d8d8d', fontSize: 16 }}>
                        <i className="fa fa-arrow-right" aria-hidden="true"></i>
                      </td>
                      <td>
                        <span className="label" style={{ backgroundColor: step.st_to_color, color: '#fff' }}>
                          {step.st_to_name}
                        </span>
                      </td>
                      <td>
                        {step.assign_user_name !== '' ? (
                          <>
                            {step.assign_user_name}
                            {step.assign_group_name !== '' && (
                              <span style={{ color: '#8d8d8d' }}> ({step.assign_group_name})</span>
                            )}
                          </>
                        ) : step.assign_group_name !== '' ? (
                          step.assign_group_name
                        ) : (
                          <span style={{ color: '#8d8d8d' }}>unchanged</span>
                        )}
                        {approvers.data?.steps?.[String(step.ID)] && (
                          <div className="od-step-approver-badge" style={{ marginTop: 4 }}>
                            <span className="label" style={{ backgroundColor: '#1f6fb2', color: '#fff' }}>
                              <i className="fas fa-user-check" aria-hidden="true"></i> Approval:{' '}
                              {approvers.data.steps[String(step.ID)].label}
                            </span>
                          </div>
                        )}
                        {cab.data?.steps?.[String(step.ID)] && (
                          <div className="od-step-cab-badge" style={{ marginTop: 4 }}>
                            <span className="label" style={{ backgroundColor: '#6f42c1', color: '#fff' }}>
                              <i className="fas fa-gavel" aria-hidden="true"></i> CAB:{' '}
                              {cab.data.steps[String(step.ID)].group_name}
                            </span>{' '}
                            <span style={{ color: '#8d8d8d', fontSize: 11 }}>
                              {cab.data.steps[String(step.ID)].rule_label.toLowerCase()}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="center">
                        <div className="btn-group" role="group">
                          <button
                            type="button"
                            className="btn btn-sm btn-default edit-step"
                            data-step={step.ID}
                            onClick={() => setEdited(step)}
                          >
                            <i className="fas fa-pencil-alt" aria-hidden="true"></i> Edit
                          </button>
                          {step.status_from > 0 && (
                            <button
                              type="button"
                              className="btn btn-sm btn-default remove-step"
                              data-step={step.ID}
                              disabled={drop.isPending}
                              onClick={() => drop.mutate(step.ID)}
                            >
                              <i className="fas fa-times" aria-hidden="true"></i> Remove
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <StepModal
        workflow={workflow.ID}
        open={creating}
        onClose={() => setCreating(false)}
        onSaved={reload}
      />
      <StepModal
        workflow={workflow.ID}
        step={edited ?? undefined}
        open={edited !== null}
        onClose={() => setEdited(null)}
        onSaved={reload}
      />
    </div>
  )
}

function WorkflowModal({
  workflow,
  open,
  onClose,
  onSaved,
}: {
  workflow?: WorkflowRow
  open: boolean
  onClose(): void
  onSaved(): void
}) {
  const editing = workflow !== undefined
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  useEffect(() => {
    if (!open) return
    setName(workflow?.workflow_name ?? '')
    setDescription(workflow?.workflow_description ?? '')
  }, [open, workflow])

  const save = useMutation({
    mutationFn: () =>
      editing ? updateWorkflow(workflow.ID, name.trim(), description) : createWorkflow(name.trim(), description),
    onSuccess: () => {
      toast(editing ? 'Workflow updated.' : 'Workflow created.', 'Workflows')
      onClose()
      onSaved()
    },
    onError: (e) =>
      toast(e instanceof ApiError && e.message ? e.message : 'Something went wrong.', 'Error', 'error'),
  })

  return (
    <Modal
      id={editing ? 'editWorkflowModal' : 'newWorkflowModal'}
      labelledBy={editing ? 'editWorkflowModalLabel' : 'newWorkflowModalLabel'}
      open={open}
      onClose={onClose}
      title={editing ? 'Edit workflow' : 'New workflow'}
    >
      <form
        id={editing ? 'edit-workflow-form' : 'new-workflow-form'}
        method="post"
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim() === '') return
          save.mutate()
        }}
      >
        <div className="form-group">
          <label className="control-label">
            Workflow name <span className="required">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            name="workflow_name"
            maxLength={60}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label className="control-label">Workflow description</label>
          <textarea
            style={{ maxWidth: '100%' }}
            className="form-control"
            name="workflow_description"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <div className="form-group aright">
          <button type="button" className="btn btn-default" onClick={onClose}>
            Close
          </button>{' '}
          <button
            type="submit"
            className={editing ? 'btn btn-primary submit-edit-workflow' : 'btn btn-primary submit-new-workflow'}
            disabled={name.trim() === '' || save.isPending}
          >
            {editing ? 'Update' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export function WorkflowSettingsPage() {
  const queryClient = useQueryClient()
  const shell = useQuery(workflowsShellQuery())
  const [search, setSearch] = useState('')
  const [start, setStart] = useState(0)
  const [creating, setCreating] = useState(false)
  const [edited, setEdited] = useState<WorkflowRow | null>(null)
  const [open, setOpen] = useState<number | null>(null)

  const table = useQuery(workflowsTableQuery(search, start, PAGE))

  const reload = () => {
    void queryClient.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'workflow_settings'] })
  }

  const drop = useMutation({
    mutationFn: (id: number) => removeWorkflow(id),
    onSuccess: () => {
      toast('Workflow removed.', 'Workflows')
      reload()
    },
    onError: () => toast('Something went wrong.', 'Error', 'error'),
  })

  if (shell.error instanceof ApiError && shell.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (shell.error) {
    return <LoadFailed error={shell.error} onRetry={() => void shell.refetch()} what="This screen" />
  }
  if (!shell.data) return <Spinner />

  const rows = table.data?.data ?? []
  const total = table.data?.recordsTotal ?? 0

  return (
    <div>
      <h4>
        <strong>Workflow</strong> settings
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage, create or edit <strong>workflows</strong>
      </label>
      <button type="button" className="btn btn-success pull-right" onClick={() => setCreating(true)}>
        <i className="fas fa-plus" aria-hidden="true"></i> Create workflow
      </button>
      <hr />

      <div className="form-group" style={{ maxWidth: 320 }}>
        <input
          type="search"
          className="form-control input-sm"
          placeholder="Search"
          value={search}
          onChange={(e) => {
            setStart(0)
            setSearch(e.target.value)
          }}
        />
      </div>

      <div className="list-group">
        {table.error ? (
          <LoadFailed error={table.error} onRetry={() => void table.refetch()} what="The list" />
        ) : !table.data ? (
          <Spinner />
        ) : (
          <table id="workflow-list" className="table table-bordered" width="100%">
            <thead>
              <tr>
                <th className="a-left">Workflow</th>
                <th className="a-left">Description</th>
                <th className="a-left">Used by</th>
                <th width={80}>Steps</th>
                <th width={180}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: 'center' }}>
                    Nothing to see yet.
                  </td>
                </tr>
              ) : (
                rows.map((workflow) => (
                  <Fragment key={workflow.ID}>
                    <tr id={`row_${workflow.ID}`}>
                      <td>
                        {/* Plusul lui DataTable in fata titlului: fara el nu se vedea ca fluxul se desface. */}
                        <a
                          className="workflow-toggle"
                          role="button"
                          tabIndex={0}
                          aria-expanded={open === workflow.ID}
                          style={{ cursor: 'pointer', display: 'inline-block' }}
                          onClick={() => setOpen(open === workflow.ID ? null : workflow.ID)}
                          onKeyDown={(ev) => {
                            if (ev.key !== 'Enter' && ev.key !== ' ') return
                            ev.preventDefault()
                            setOpen(open === workflow.ID ? null : workflow.ID)
                          }}
                        >
                          <i
                            className={`fas ${open === workflow.ID ? 'fa-minus-circle' : 'fa-plus-circle'}`}
                            aria-hidden="true"
                            style={{ marginRight: 6 }}
                          ></i>
                          {workflow.workflow_name}
                        </a>
                      </td>
                      <td>{workflow.workflow_description}</td>
                      <td>
                        {workflow.used_by.length === 0 ? (
                          <span style={{ color: '#8d8d8d' }}>no ticket type</span>
                        ) : (
                          workflow.used_by.map((type) => (
                            <span className="label label-default" key={type} style={{ marginRight: 4 }}>
                              {type}
                            </span>
                          ))
                        )}
                      </td>
                      <td className="center">
                        <span className="label label-info">{workflow.steps}</span>
                      </td>
                      <td className="center">
                        <button
                          type="button"
                          className="btn btn-xs btn-default"
                          data-workflow={workflow.ID}
                          onClick={() => setEdited(workflow)}
                        >
                          <i className="fas fa-pencil-alt" aria-hidden="true"></i> Edit
                        </button>{' '}
                        <button
                          type="button"
                          className="btn btn-xs btn-danger remove-workflow"
                          data-workflow={workflow.ID}
                          disabled={drop.isPending}
                          onClick={() => drop.mutate(workflow.ID)}
                        >
                          <i className="fas fa-trash" aria-hidden="true"></i> Remove
                        </button>
                      </td>
                    </tr>
                    {open === workflow.ID && (
                      <tr>
                        <td colSpan={5}>
                          <WorkflowDetails workflow={workflow} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {total > PAGE && (
        <div className="aright">
          <button
            type="button"
            className="btn btn-xs btn-default"
            disabled={start === 0}
            onClick={() => setStart(Math.max(0, start - PAGE))}
          >
            Previous
          </button>{' '}
          <span>
            {start + 1}-{Math.min(start + PAGE, total)} of {total}
          </span>{' '}
          <button
            type="button"
            className="btn btn-xs btn-default"
            disabled={start + PAGE >= total}
            onClick={() => setStart(start + PAGE)}
          >
            Next
          </button>
        </div>
      )}

      <WorkflowModal open={creating} onClose={() => setCreating(false)} onSaved={reload} />
      <WorkflowModal
        workflow={edited ?? undefined}
        open={edited !== null}
        onClose={() => setEdited(null)}
        onSaved={reload}
      />
    </div>
  )
}
